import { useMutation, useQueryClient, mutationOptions } from '@tanstack/react-query';

import { contactListsKeys } from '#contactListFeature/models/keys';

import {
	createEmailCampaign,
	updateEmailCampaign,
	deleteEmailCampaign,
	upsertEmailListsOfCampaign,
	removeRecipientFromCampaign,
} from '../../api/emailCampaignsApi';
import { emailSendsKeys } from '../emailSends/keys';

import { emailCampaignsKeys, emailCampaignsMutationKeys } from './keys';

export const createEmailCampaignOptions = (queryClient) =>
	mutationOptions({
		mutationKey: emailCampaignsMutationKeys.create,
		mutationFn: ({ name, campaignType }) => createEmailCampaign({ name, campaignType }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: emailCampaignsKeys.list() });
		},
	});

export function useCreateEmailCampaign() {
	const qc = useQueryClient();
	return useMutation(createEmailCampaignOptions(qc));
}

export const updateEmailCampaignOptions = (queryClient) =>
	mutationOptions({
		mutationKey: emailCampaignsMutationKeys.update,
		mutationFn: ({ id, updates, prevStatus }) =>
			updateEmailCampaign({
				id,
				updates,
				prevStatus,
			}),
		onMutate: async ({ id, updates }) => {
			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: emailCampaignsKeys.list() });

			// Snapshot the previous value
			const previousCampaigns = queryClient.getQueryData(emailCampaignsKeys.list());

			// Optimistically update to the new value
			queryClient.setQueryData(emailCampaignsKeys.list(), (old) => {
				if (!old) return old;
				return old.map((campaign) => (campaign.id === id ? { ...campaign, ...updates } : campaign));
			});

			return { previousCampaigns };
		},
		onError: (err, variables, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousCampaigns) {
				queryClient.setQueryData(emailCampaignsKeys.list(), context.previousCampaigns);
			}
		},
		onSettled: (data, error, variables) => {
			// Always refetch after error or success to ensure we have the latest data
			queryClient.invalidateQueries({ queryKey: emailCampaignsKeys.all });

			// If campaign status was changed to 'active', invalidate email sends to refresh statistics
			if (variables.updates?.campaignStatus === 'active') {
				queryClient.invalidateQueries({ queryKey: emailSendsKeys.all });
			}
		},
	});

export function useUpdateEmailCampaign() {
	const qc = useQueryClient();
	return useMutation(updateEmailCampaignOptions(qc));
}

export const deleteEmailCampaignOptions = (queryClient) =>
	mutationOptions({
		mutationKey: emailCampaignsMutationKeys.delete,
		mutationFn: (id) => deleteEmailCampaign(id),
		onMutate: async (id) => {
			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey: emailCampaignsKeys.list() });

			// Snapshot the previous value
			const previousCampaigns = queryClient.getQueryData(emailCampaignsKeys.list());

			// Optimistically remove the campaign
			queryClient.setQueryData(emailCampaignsKeys.list(), (old) => {
				if (!old) return old;
				return old.filter((campaign) => campaign.id !== id);
			});

			return { previousCampaigns };
		},
		onError: (err, id, context) => {
			// If the mutation fails, use the context returned from onMutate to roll back
			if (context?.previousCampaigns) {
				queryClient.setQueryData(emailCampaignsKeys.list(), context.previousCampaigns);
			}
		},
		onSettled: () => {
			// Always refetch after error or success
			queryClient.invalidateQueries({ queryKey: emailCampaignsKeys.all });
		},
	});

export function useDeleteEmailCampaign() {
	const qc = useQueryClient();
	return useMutation(deleteEmailCampaignOptions(qc));
}

export const upsertEmailListsOfCampaignOptions = (queryClient) =>
	mutationOptions({
		mutationKey: emailCampaignsMutationKeys.upsertLists,
		mutationFn: ({ campaignId, listIds }) => upsertEmailListsOfCampaign(campaignId, listIds),
		onSuccess: (_, { campaignId }) => {
			queryClient.invalidateQueries({ queryKey: emailCampaignsKeys.detail(campaignId) });
			queryClient.invalidateQueries({ queryKey: contactListsKeys.campaignRecipients(campaignId) });
		},
	});

export function useUpsertEmailListsOfCampaign() {
	const qc = useQueryClient();
	return useMutation(upsertEmailListsOfCampaignOptions(qc));
}

export function useRemoveRecipientFromCampaign() {
	const qc = useQueryClient();
	return useMutation({
		mutationKey: emailCampaignsMutationKeys.removeRecipient,
		mutationFn: ({ campaignId, contactPersonId }) => removeRecipientFromCampaign(campaignId, contactPersonId),
		onSuccess: (_data, { campaignId }) => {
			qc.invalidateQueries({ queryKey: contactListsKeys.campaignRecipients(campaignId) });
		},
	});
}
